import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Outlet } from 'react-router';

import { Navigate, useTypedParams } from '@sorare/routing';
import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_QUICK_PLAY_TRCK,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { RequireAuth } from '@sorare/core/src/routing/RequireAuth';

import { QuickPlayTutorialDialog } from '#src/components/quickPlay/QuickPlayTutorialDialog/index.tsx';
import { FixtureTabLayout } from '#src/layouts/FixtureLayout/FixtureTabLayout/index.tsx';

import type {
  QuickPlayTasksQuery,
  QuickPlayTasksQueryVariables,
} from './__generated__/layout.graphql.ts';
import type { LayoutParams } from './__generated__/routeParams.ts';

const QUICK_PLAY_TASK_TRCK_QUERY = gql`
  query QuickPlayTasksQuery($sport: Sport!) {
    currentUser {
      slug
      id
      tasksTracks(sport: $sport) {
        id
      }
    }
  }
` as TypedDocumentNode<QuickPlayTasksQuery, QuickPlayTasksQueryVariables>;

export const loader = queryLoader(QUICK_PLAY_TASK_TRCK_QUERY, ({ sport }) => ({
  sport,
}));

const Layout = () => {
  const {
    flags: {
      useQuickPlay = {
        [Sport.BASEBALL]: false,
        [Sport.NBA]: false,
        [Sport.FOOTBALL]: false,
      },
    },
  } = useFeatureFlags();
  const { generateSportContextPath, sport } = useSportContext();
  const { lifecycle, update, loading } = useLifecycle();

  const { trck } = useTypedParams<LayoutParams>();
  const { data } = useQueryLoader<QuickPlayTasksQuery>();
  const trckId = data?.currentUser?.tasksTracks[0]?.id;

  if (!useQuickPlay[sport]) {
    return <Navigate to={generateSportContextPath(ANY_SPORT_PLAY)} replace />;
  }

  if (!lifecycle?.sawQuickPlayTutorial) {
    return (
      <RequireAuth>
        <QuickPlayTutorialDialog
          open
          loading={loading}
          onClose={() => {
            update(LIFECYCLE.sawQuickPlayTutorial, true);
          }}
        />
      </RequireAuth>
    );
  }

  if (trckId && !trck) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_PLAY_QUICK_PLAY_TRCK, {
          params: {
            trck: idFromObject(trckId),
          },
        })}
        keepSearchParams
        replace
      />
    );
  }
  return (
    <RequireAuth>
      <FixtureTabLayout
        so5FixtureSlug="ongoing"
        eventType={So5FixtureEvent.CLASSIC}
      >
        <Outlet />
      </FixtureTabLayout>
    </RequireAuth>
  );
};

export default Layout;
